import Divider from '@/components/common/Divider';
import {
    BidAsks,
    MarketInstrument,
    MarketListHeader,
    UserMarketProps
} from '@/components/modules/MarketList/UserMarket';
import { switchOrderBook } from '@/services/MarketService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectActiveMarketPair } from '@/state/reducers/marketPairSlice';
import {
    addSubscription,
    removeSubscription,
    selectCurrentWatchList,
    selectTraderWatchLists,
    setShouldReinstateWatchlist
} from '@/state/reducers/traderMarketSlice';
import cn from 'classnames';
import { forwardRef, Fragment, useMemo } from 'react';
import { MdAddCircleOutline, MdOutlineRemoveRedEye, MdRemoveCircleOutline } from 'react-icons/md';

const TraderUserMarket = forwardRef<HTMLDivElement, UserMarketProps>((props: UserMarketProps, ref) => {
    const { market, style, isActive, onClick, onActive } = props;
    const dispatch = useAppDispatch();
    const activePair = useAppSelector(selectActiveMarketPair);
    const watchLists = useAppSelector(selectTraderWatchLists);
    const selectedWatchList = useAppSelector(selectCurrentWatchList);
    const watchList = useMemo(() => selectedWatchList || watchLists[0], [watchLists, selectedWatchList]);

    const isActivePair = useMemo(() => {
        return activePair?.celer === market.celer;
    }, [activePair, market]);

    const isInWatchlist = useMemo(() => {
        return watchList.markets.some((m) => m.celer === market.celer);
    }, [watchList]);

    return (
        <Fragment>
            {market.celer === 'header' ? (
                <MarketListHeader ref={ref} style={style} />
            ) : (
                <div
                    ref={ref}
                    style={style}
                    className={cn('hover:bg-brand-neutral-700', {
                        'bg-brand-background-dark': isActive
                    })}>
                    <div
                        className={cn('cursor-pointer flex text-xs py-1.5 px-2 text-brand-text justify-between')}
                        onClick={onActive}>
                        <MarketInstrument {...props} />
                        <BidAsks {...props} />
                    </div>
                    {isActive && <Divider className="bg-brand-neutral-700" />}
                    {isActive && (
                        <div className="flex text-brand-text text-xs h-[28px] border-b border-brand-neutral-700 gap-0.5 bg-brand-background-dark">
                            <div
                                className={cn('flex justify-center items-center gap-1 whitespace-nowrap px-2', {
                                    'cursor-pointer bg-brand-primary text-brand-text hover:bg-brand-primary-light':
                                        !isActivePair,
                                    'cursor-not-allowed bg-brand-primary text-brand-text': isActivePair
                                })}
                                onClick={() => {
                                    switchOrderBook(market);
                                    onClick && onClick();
                                }}>
                                <MdOutlineRemoveRedEye className="w-3.5 h-3.5" />
                                <span className="">View Chart</span>
                            </div>
                            {!isInWatchlist ? (
                                <div
                                    className="flex justify-center items-center gap-1 cursor-pointer whitespace-nowrap text-brand-text bg-brand-primary hover:bg-brand-primary-light px-2"
                                    onClick={() => {
                                        dispatch(addSubscription({ id: watchList.id, market }));
                                        dispatch(setShouldReinstateWatchlist({ mode: 'add', market }));
                                    }}>
                                    <MdAddCircleOutline className="w-3.5 h-3.5" />
                                    <span className="">Add Ticker</span>
                                </div>
                            ) : (
                                <div
                                    className="flex justify-center items-center gap-1 cursor-pointer whitespace-nowrap bg-brand-red hover:bg-brand-red-light text-brand-text px-2"
                                    onClick={() => {
                                        dispatch(removeSubscription({ id: watchList.id, market }));
                                        dispatch(setShouldReinstateWatchlist({ mode: 'remove', market }));
                                    }}>
                                    <MdRemoveCircleOutline className="w-3.5 h-3.5" />
                                    <span className="">Remove Ticker</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
});

TraderUserMarket.displayName = 'TraderUserMarket';

export default TraderUserMarket;
